import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqItem } from '../../../models/faq.model';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}

@Component({
  selector: 'app-software-development',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './software-development.component.html',
  styleUrl: './software-development.component.scss'
})
export class SoftwareDevelopmentComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('featuresSection') featuresSection!: ElementRef;
  @ViewChild('whyChooseSection') whyChooseSection!: ElementRef;
  isFeaturesVisible = false;
  isWhyChooseVisible = false;
  private observer: IntersectionObserver | null = null;
  // To track the injected script
  private jsonLdScript: any;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const softwareDevSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/custom-software-development#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/custom-software-development",
          "name": "Custom Software Development Services | AI-Powered Enterprise Solutions | Kalpita Technologies",
          "description": "AI-powered, scalable, and secure custom software development services for enterprises. Full-cycle development with 25% faster time-to-market.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US",
          "datePublished": "2024-01-01"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/custom-software-development#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "Custom Software Development", "item": "https://www.kalpitatechnologies.com/company-services/custom-software-development" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/custom-software-development#service",
          "name": "Custom Software Development Services",
          "serviceType": "Custom Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "AI-powered, scalable, and secure custom software development services designed for enterprises and startups."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/custom-software-development#process",
          "name": "Our Proven 5-Step Custom Software Development Process",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Discovery & Strategy", "text": "Deep dive into business needs and define a strategy." },
            { "@type": "HowToStep", "position": 2, "name": "Design & Prototyping", "text": "Create intuitive wireframes and detailed mock-ups." },
            { "@type": "HowToStep", "position": 3, "name": "Agile Development", "text": "Break project into sprints delivering incremental results." },
            { "@type": "HowToStep", "position": 4, "name": "QA & Testing", "text": "Rigorously test every feature ensuring it is bug-free." },
            { "@type": "HowToStep", "position": 5, "name": "Launch & Support", "text": "Smooth deployment and continuous post-launch maintenance." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/custom-software-development#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How much does custom software development cost?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It depends on scope and features. On average, projects range from $10,000+. We provide a transparent quote after the discovery phase."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(softwareDevSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }

  goToCaseStudies(): void { }

  softwaredevFaqs: FaqItem[] = [
    {
      question: 'How much does custom software development cost?',
      answer: `It depends on scope and features. On average, projects range from $10000+. 
      We’ll give you a transparent quote after the discovery phase.`
    },
    {
      question: 'How long will it take?',
      answer: `Most projects take 3 to 9 months, depending on complexity. Agile delivery ensures 
      you see progress every 2–3 weeks.`
    },
    {
      question: ' Can you work with my existing systems?',
      answer: `Yes, we specialize in integration with ERPs, CRMs, and legacy systems.`
    },
    {
      question: 'What industries do you specialize in?',
      answer: `We have deep expertise in Insurtech, Fintech, Healthcare, E-commerce, and Hi-Tech.`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'Unified Digital Portal Solution ',
      description: 'A leading enterprise partnered with Kalpita to build a unified portal, bringing HR, compliance, and self-service under one platform. The result: seamless collaboration, real-time data accessibility, and a significant boost in employee engagement, all delivered with agile precision.',
      image: '../../../../assets/services/software-dev/successStories/Unified-Digital-Portal-Solution.png'
    },
    {
      id: 2,
      year: '2023',
      title: 'Modernizing US Retirement Plan Analytics ',
      description: 'Our team developed a scalable analytics system for a US-based financial firm, automating intricate reporting processes and reducing manual workloads. With this solution, advisors gained actionable insights and delivered timely, error-free client reporting.',
      image: '../../../../assets/services/software-dev/successStories/Modernizing-US-Retirement-Plan-Analytics.png'
    },
    {
      id: 3,
      year: '2025',
      title: 'AI-Powered Insurance Application',
      description: 'Harnessing the latest in AI and cloud, Kalpita engineered an insurance application that automates claims processing and fraud detection. The application slashed processing times, improved accuracy, and delivered outstanding customer experiences.',
      image: '../../../../assets/services/software-dev/successStories/AI-Powered-Insurance-Application.png'
    }
  ];

  selectedIndex = 1;

  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];
    if (this.selectedIndex > 0) {
      result.push({ card: this.timelinecards[this.selectedIndex - 1], position: 'top' as const });
    }
    result.push({ card: this.timelinecards[this.selectedIndex], position: 'center' as const });
    if (this.selectedIndex < this.timelinecards.length - 1) {
      result.push({ card: this.timelinecards[this.selectedIndex + 1], position: 'bottom' as const });
    }
    return result;
  }

  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/software-dev/whyChooseUs/deepdomain.png',
      title: 'Deep Domain Expertise in Custom Solutions',
      description: 'For over 7+ years, we’ve been building custom software applications for industries like Insurtech, Fintech, Healthcare, E-commerce, and Hi-Tech. Every solution is custom-made to fit business workflows, not force you into rigid systems.'
    },
    {
      icon: '../../../../assets/services/software-dev/whyChooseUs/scalablecustomsoftware.png',
      title: 'Scalable Custom Software for Long-Term Growth',
      description: 'Our custom solutions are future-ready, whether you need to expand user capacity, add new integrations, or move to the cloud. We design scalable architectures that adapt as your business grows.'
    },
    {
      icon: '../../../../assets/services/software-dev/whyChooseUs/certified_secure.png',
      title: 'Certified, Secure & Reliable Development',
      description: 'As a SOC II Certified company, we follow strict compliance and data security standards, implementing security across the SSDLC. Our security-trained teams deliver rigorously verified, enterprise-grade secure software from day one.'
    },
    {
      icon: '../../../../assets/services/software-dev/whyChooseUs/fullcycle.png',
      title: 'Full-Cycle Custom Software Services',
      description: 'From idea discovery and prototyping to custom coding, testing, deployment, and maintenance, we handle the entire lifecycle. You get a single partner for end-to-end custom software development services.'
    },
    {
      icon: '../../../../assets/services/software-dev/whyChooseUs/fastertime.png',
      title: 'Faster Time-to-Market with Agile Custom Development',
      description: 'We use an agile, sprint-based model designed specifically for custom software development projects. This allows us to deliver working features quickly, validate them with you, and ensure 25% faster launch times compared to traditional methods.'
    },
    {
      icon: '../../../../assets/services/software-dev/whyChooseUs/globalteams.png',
      title: 'Global Teams, Localized Support for Custom Projects',
      description: 'With development teams in Bangalore (India) and support in the United States, we deliver custom software development services that combine cost efficiency with real-time communication.'
    }
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/software-dev/developmentProcess/Discovery_Strategy.png',
      title: 'Discovery & Strategy',
      description: 'We dive deep into your business needs, conducting thorough research and understanding your challenges. Together, we define a strategy that ensures that the software meets your objectives.'
    },
    {
      icon: '../../../../assets/services/software-dev/developmentProcess/Design_Prototyping.png',
      title: 'Design, Architecting & Prototyping',
      description: 'Our design team creates intuitive wireframes and detailed mock-ups that visualize your software and align with modern microservices architecture. We focus on seamless user experiences, transforming your ideas into scalable digital solutions.'
    },
    {
      icon: '../../../../assets/services/software-dev/developmentProcess/AgileDevelopment.png',
      title: 'Agile Development',
      description: 'Using agile methodology, we break the project into manageable sprints, delivering incremental results. Testing is done throughout to ensure functionality and quality at every step.'
    },
    {
      icon: '../../../../assets/services/software-dev/developmentProcess/QualityAssurance_Testing.png',
      title: 'Quality Assurance & Testing',
      description: 'We rigorously test every feature using strong manual and automated testing practices to ensure the software is secure, stable, and bug-free. Our QA experts perform comprehensive checks to deliver reliable software with a flawless user experience.'
    },
    {
      icon: '../../../../assets/services/software-dev/developmentProcess/Launch_Support.png',
      title: 'Launch & Support',
      description: 'We handle the smooth deployment of your software and offer continuous support. Post-launch, we provide regular updates and maintenance to keep your system running seamlessly.'
    }
  ];

  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3;

  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void { }
  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;

        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  // New Logic for Scroll Animation
  setupScrollAnimation(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          // Check which section is intersecting
          if (this.featuresSection && entry.target === this.featuresSection.nativeElement) {
            this.isFeaturesVisible = true;
            observer.unobserve(entry.target);
          }

          if (this.whyChooseSection && entry.target === this.whyChooseSection.nativeElement) {
            this.isWhyChooseVisible = true;
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% of the section is visible
    });

    if (this.featuresSection) {
      observer.observe(this.featuresSection.nativeElement);
    }

    if (this.whyChooseSection) {
      observer.observe(this.whyChooseSection.nativeElement);
    }

    this.observer = observer;
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupTiltEffect();
    this.setupScrollAnimation();
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}